/**
 * Pipeline Stage Projects - Producer/Consumer Pattern
 *
 * PRODUCER: useGetBatchPipelineStageProjects
 *   - Batch fetches all stages with their first page of projects in one request
 *   - Populates individual stage caches via setQueryData
 *   - Should be called at the page level before rendering individual stages
 *
 * CONSUMER: useGetPipelineStageInfiniteProjects
 *   - Reads from the cache populated by the producer (enabled: false)
 *   - Provides fetchNextPage() for pagination after initial batch
 */

import {
  InfiniteData,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { gqlClient } from '@/api/graphql-client';
import pipelineKeys from '@/api/queries/pipelines/keyFactory';
import { graphql } from '@/gql';
import {
  GetPipelineStageProjectsInput,
  GetPipelineStageQuery,
} from '@/gql/graphql';
import { PAGINATION_ARG_DEFAULTS } from '@payaca/shared-isomorphic/constants/graphql-pagination';

const INITIAL_PROJECTS_PER_STAGE = 10;

graphql(`
  fragment PipelineStageProjectPage on CursorPaginatedProjectsResult {
    items {
      id
      publicId
      reference
      latestActivityAt
      pipeline {
        id
        publicId
        colour
      }
      customer {
        id
        name
      }
      addresses {
        site {
          id
          line1
          line2
          city
          postalCode
          country
        }
      }
      owner {
        id
        publicId
        firstName
        lastName
        avatarUrl
        colour
        email
      }
      tags {
        id
        publicId
        name
        colour
      }
      totals {
        projectValue {
          value
          currency {
            code
            exponent
          }
        }
      }
      aggregations {
        isAnyProposalOrInvoiceBounced
        isAnyInvoiceOverdue
        hasCustomerViewedAllSentProposalsAndInvoices
        incompleteTasksCount
      }
      pipelineStageTransitionAuditLogs(
        pagination: { limit: 1, offset: 0 }
        input: { successFilter: SUCCESSFUL }
      ) {
        items {
          id
          enteredTargetStageAt
        }
      }
    }
    totalCount
    totalProjectValue {
      value
      currency {
        code
        exponent
      }
    }
    after
    first
    hasNextPage
  }
`);

type PipelineStageInfiniteData = InfiniteData<
  GetPipelineStageQuery,
  string | undefined
>;

type GetPipelineStageResponse = GetPipelineStageQuery['pipelineStage'];
export type GetPipelineStageProjectResponse = NonNullable<
  GetPipelineStageQuery['pipelineStage']
>['projects']['items'][0];

/**
 * Batch query for fetching all stages with their first page of projects.
 * Used by the producer hook for initial load.
 */
const GET_PIPELINE_WITH_STAGE_PROJECTS = graphql(`
  query GetPipelineWithStageProjects(
    $id: ID!
    $pagination: CursorPaginationInput!
    $input: GetPipelineStageProjectsInput
  ) {
    pipeline(id: $id) {
      id
      publicId
      stages {
        id
        publicId
        projects(pagination: $pagination, input: $input) {
          ...PipelineStageProjectPage
        }
      }
    }
  }
`);

/**
 * Single stage query for fetching additional pages.
 * Used by the consumer hook for fetchNextPage() pagination.
 */
const GET_PIPELINE_STAGE = graphql(`
  query GetPipelineStage(
    $id: ID!
    $pagination: CursorPaginationInput!
    $input: GetPipelineStageProjectsInput
  ) {
    pipelineStage(id: $id) {
      id
      publicId
      projects(pagination: $pagination, input: $input) {
        ...PipelineStageProjectPage
      }
    }
  }
`);

/**
 * PRODUCER: Batch fetches all pipeline stages with their first page of projects
 * in a single request, then populates individual stage caches via setQueryData.
 *
 * @see useGetPipelineStageInfiniteProjects - the consumer that reads from this cache
 */
export const useGetBatchPipelineStageProjects = (args: {
  pipelinePublicId: string | undefined;
  input?: GetPipelineStageProjectsInput;
}) => {
  const { pipelinePublicId, input } = args;
  const queryClient = useQueryClient();

  // Do not expose `data` object
  const { data: _data, ...rest } = useQuery({
    enabled: !!pipelinePublicId,
    queryKey: pipelineKeys.pipelineBatchPrefetch(pipelinePublicId!, input),
    queryFn: async ({ signal }) => {
      const data = await gqlClient.request({
        document: GET_PIPELINE_WITH_STAGE_PROJECTS,
        variables: {
          id: pipelinePublicId!,
          pagination: { first: INITIAL_PROJECTS_PER_STAGE },
          input: input,
        },
        signal,
      });

      // Populate each stage's infinite query cache (the real source of truth)
      data.pipeline?.stages.forEach((stage) => {
        // Transform the batch response to match the infinite query structure
        const infiniteData: PipelineStageInfiniteData = {
          pages: [
            {
              pipelineStage: {
                id: stage.id,
                publicId: stage.publicId,
                projects: stage.projects,
              },
            },
          ],
          pageParams: [undefined],
        };

        queryClient.setQueryData(
          pipelineKeys.pipelineStageProjects(
            pipelinePublicId!,
            stage.publicId,
            input
          ),
          infiniteData
        );
      });

      // Return minimal metadata - not used as source of truth
      return {
        fetchedAt: Date.now(),
        stageCount: data.pipeline?.stages.length ?? 0,
      };
    },
  });

  return rest;
};

type UseGetPipelineStageInfiniteProjectsOptions = {
  pipelinePublicId: string;
  pipelineStagePublicId: string;
  input?: GetPipelineStageProjectsInput;
};

const filterProjectsByInput = (
  project: GetPipelineStageProjectResponse,
  input: GetPipelineStageProjectsInput
): boolean => {
  if (input.assignedTo?.length) {
    if (!project.owner || !input.assignedTo.includes(project.owner.publicId)) {
      return false;
    }
  }

  if (input.tagIds?.length) {
    const projectTagIds = project.tags.map((tag) => tag.publicId);
    const hasMatchingTag = input.tagIds.some((tagId) =>
      projectTagIds.includes(tagId)
    );
    if (!hasMatchingTag) {
      return false;
    }
  }

  return true;
};

/**
 * CONSUMER: Reads pipeline stage projects from cache populated by useGetBatchPipelineStageProjects.
 *
 * This hook uses `enabled: false`, so React Query will never automatically run `queryFn`.
 * It only reads the initial data from the cache populated by the producer; it does not
 * perform an initial fetch itself. The consumer can only load additional pages manually
 * by calling `fetchNextPage()`.
 *
 * @see useGetBatchPipelineStageProjects - the producer that populates the cache
 */
export const useGetPipelineStageInfiniteProjects = (
  args: UseGetPipelineStageInfiniteProjectsOptions
) => {
  const { pipelinePublicId, pipelineStagePublicId, input } = args;

  const queryFn = ({ pageParam }: { pageParam?: string }) => {
    return gqlClient.request(GET_PIPELINE_STAGE, {
      id: pipelineStagePublicId,
      pagination: {
        after: pageParam,
        first: PAGINATION_ARG_DEFAULTS.limit,
      },
      input: input,
    });
  };

  const { data, ...rest } = useInfiniteQuery({
    // Cache is populated by useGetBatchPipelineStageProjects - this hook reads from that cache
    enabled: false,
    queryKey: pipelineKeys.pipelineStageProjects(
      pipelinePublicId,
      pipelineStagePublicId,
      input
    ),
    queryFn,
    placeholderData: (prev) => {
      if (input?.searchTerm || !prev) return prev;

      if (!input || (!input.assignedTo?.length && !input.tagIds?.length)) {
        return prev;
      }

      const filteredPages = prev.pages.map((page) => {
        if (!page.pipelineStage) return page;

        const filteredItems = page.pipelineStage.projects.items.filter(
          (project) => filterProjectsByInput(project, input)
        );

        return {
          ...page,
          pipelineStage: {
            ...page.pipelineStage,
            projects: {
              ...page.pipelineStage.projects,
              items: filteredItems,
              totalCount: filteredItems.length,
              hasNextPage: false,
            },
          },
        };
      });

      return {
        pages: filteredPages,
        pageParams: prev.pageParams,
      };
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => {
      if (!lastPage.pipelineStage?.projects.hasNextPage) {
        return undefined;
      }

      return lastPage.pipelineStage.projects.items[
        lastPage.pipelineStage.projects.items.length - 1
      ]?.publicId;
    },
  });

  return {
    data,
    pipelineStageProjects: data?.pages.flatMap(
      (page) => page.pipelineStage?.projects.items ?? []
    ),
    totalCount: data?.pages[0]?.pipelineStage?.projects.totalCount,
    totalProjectValue:
      data?.pages[0]?.pipelineStage?.projects.totalProjectValue,
    ...rest,
  };
};
